/**
 * JWT utility functions for decoding tokens and extracting user information
 */

// Helper function to get cookie value
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
};

// Helper function to get auth token from localStorage or cookie
const getAuthToken = () => {
  let token = localStorage.getItem("authToken");

  // If not in localStorage, try to get from cookie
  if (!token) {
    token = getCookie("authToken");

    // If found in cookie, store in localStorage for future use
    if (token) {
      localStorage.setItem("authToken", token);
      console.log(
        "[jwtUtils] Token retrieved from cookie and cached in localStorage",
      );
    }
  }

  return token;
};

export const decodeJWT = (token) => {
  try {
    if (!token) return null;

    // JWT has 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Decode the payload (second part)
    const payload = parts[1];

    // Add padding if needed (base64 requires length to be multiple of 4)
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);

    // Decode from base64
    const decodedPayload = atob(paddedPayload);

    // Parse JSON
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

export const getUserRoles = () => {
  // Get roles directly from localStorage instead of JWT token
  const rolesFromStorage = localStorage.getItem("roles");

  if (rolesFromStorage) {
    try {
      // Parse the roles array from localStorage
      const roles = JSON.parse(rolesFromStorage);
      console.log("[jwtUtils] Roles retrieved from localStorage:", roles);
      return Array.isArray(roles) ? roles : [];
    } catch (error) {
      console.error("Error parsing roles from localStorage:", error);
      // If parsing fails, fall back to JWT token method
    }
  }

  // Fallback: if no roles in localStorage, try to get from JWT token
  console.log("[jwtUtils] No roles in localStorage, falling back to JWT token");
  const token = getAuthToken();
  const decoded = decodeJWT(token);
  return decoded?.roles || [];
};

export const getUserInfoFromToken = () => {
  const token = getAuthToken();
  const decoded = decodeJWT(token);
  return decoded || null;
};

export const isTokenExpired = () => {
  // If authentication is starting or in progress, assume token is valid
  if (window.authenticationStarting || window.authenticationPromise) {
    console.log(
      "[jwtUtils] Authentication starting/in progress, assuming token is valid",
      {
        authenticationStarting: window.authenticationStarting,
        authenticationPromise: !!window.authenticationPromise,
        calledFrom: new Error().stack.split("\n")[2],
      },
    );
    return false;
  }

  // Get JWT expiry from localStorage first
  const jwtExpiryFromStorage = localStorage.getItem("jwtExpiry");

  if (jwtExpiryFromStorage) {
    try {
      const expiryTimestamp = parseInt(jwtExpiryFromStorage, 10);
      const isExpired = Date.now() >= expiryTimestamp;
      console.log("[jwtUtils] JWT expiry checked from localStorage:", {
        expiryDate: new Date(expiryTimestamp),
        currentDate: new Date(),
        isExpired,
      });
      return isExpired;
    } catch (error) {
      console.error("Error parsing JWT expiry from localStorage:", error);
      // Fall back to JWT token method
    }
  }

  // Fallback: if no expiry in localStorage, try to get from JWT token
  console.log(
    "[jwtUtils] No JWT expiry in localStorage, falling back to JWT token",
  );
  const token = localStorage.getItem("authToken");

  // During initial load or when no auth data exists, check if we have user data suggesting active session
  const userUuid = localStorage.getItem("uuid") || getCookie("uuid");
  const hasUserSession = Boolean(userUuid);

  if (hasUserSession && !jwtExpiryFromStorage && !token) {
    console.log(
      "[jwtUtils] User session exists but no JWT data, likely HttpOnly cookie scenario",
    );
    return false; // Assume valid when we have user session but no JWT access
  }
  // No jwtExpiry in localStorage, no authToken in localStorage, no user session
  // → user is not authenticated (or was fully logged out). Don't report as "expired"
  // because that would incorrectly trigger logoutUser() and a backend logout call.
  // Return false so callers treat the session as "not present" rather than "timed out".
  if (!token) {
    return false;
  }

  const decoded = decodeJWT(token);

  if (!decoded || !decoded.exp) return true;

  // exp is in seconds, Date.now() is in milliseconds
  return decoded.exp * 1000 < Date.now();
};

// Get JWT expiry date from localStorage
export const getJwtExpiry = () => {
  const jwtExpiryFromStorage = localStorage.getItem("jwtExpiry");

  if (jwtExpiryFromStorage) {
    try {
      const expiryTimestamp = parseInt(jwtExpiryFromStorage, 10);
      return new Date(expiryTimestamp);
    } catch (error) {
      console.error("Error parsing JWT expiry from localStorage:", error);
    }
  }

  // Fallback: get from JWT token
  const token = localStorage.getItem("authToken");
  const decoded = decodeJWT(token);

  if (decoded && decoded.exp) {
    return new Date(decoded.exp * 1000);
  }

  return null;
};

// Helper function to clear authentication data
export const clearAuthData = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("roles");
  localStorage.removeItem("jwtExpiry");
  localStorage.removeItem("alertLevel"); // Clear alert level preference
  localStorage.removeItem("app-theme"); // Clear app theme preference
  localStorage.removeItem("userVerified"); // Clear user verification status
  localStorage.removeItem("verifiedUserUuid"); // Clear verified user UUID
  localStorage.removeItem("language"); // Clear language so next user defaults to English
  localStorage.removeItem("ada-contrast");
  localStorage.removeItem("ada-high-contrast");
  localStorage.removeItem("analyticsEnabled");
  localStorage.removeItem("dashboardMode");
  localStorage.removeItem("dateFormat");
  localStorage.removeItem("emailMarketing");
  localStorage.removeItem("emailSecurityAlerts");
  localStorage.removeItem("emailTrainingReminders");
  localStorage.removeItem("emailWeeklyDigest");
  localStorage.removeItem("gettingStarted_explored");
  localStorage.removeItem("landingPage");
  localStorage.removeItem("loginNotificationsEnabled");
  localStorage.removeItem("profileVisibility");
  localStorage.removeItem("pushNotificationsEnabled");
  localStorage.removeItem("showViewSettings");
  localStorage.removeItem("theme");
  localStorage.removeItem("timeFormat");
  localStorage.removeItem("timezone");
  localStorage.removeItem("tryitout_fingerprint");
  console.log("[jwtUtils] Authentication data cleared from localStorage");
};
